/**
 * Rhythmz — Scene: Fractal Dragon
 * ──────────────────────────────────
 * A serpentine dragon with a tube body along a sine wave path, wings that
 * flap with bass, scales along the body, and a horned head.
 *
 * Audio: Bass → wing flap + body undulation. Beat → head snap.
 */

import * as THREE from 'three/webgpu';
import { Fn, vec4, float, fract, pow, mul, add, positionLocal } from 'three/tsl';
import { SceneLighting, type AudioRef } from './SceneLighting';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface DragonUniforms {
  uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N;
  uCol0: N; uCol1: N; uCol2: N;
}

export function createFractalDragon(u: DragonUniforms): SceneHandle {
  const group = new THREE.Group();
  const lighting = new SceneLighting({
    keyColor: new THREE.Color(1.0, 0.8, 0.3),
    keyIntensity: 10, keyPosition: new THREE.Vector3(3, 5, 4),
    fillColor: new THREE.Color(0.8, 0.2, 0.1),
    fillIntensity: 4, fillPosition: new THREE.Vector3(-3, -1, 3),
    rimColor: new THREE.Color(0.2, 0.4, 0.9),
    rimIntensity: 6, rimPosition: new THREE.Vector3(0, -2, -5),
    camLightIntensity: 2,
  });
  group.add(lighting.group);

  const dragonMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.4, roughness: 0.3, color: 0x4a1010 });
  dragonMat.emissiveNode = Fn(() => {
    const seam = pow(fract(mul(positionLocal.y, float(3.0))), float(6.0));
    const glow = mul(seam, add(float(0.2), mul(u.uBass, float(0.6))));
    return vec4(mul(float(1.0), glow), mul(float(0.3), glow), float(0.0), float(1.0));
  })();

  // ── Body: tube along sine path ──
  const bodySegments = 30;
  const bodyPoints: THREE.Vector3[] = [];
  for (let i = 0; i <= bodySegments; i++) {
    const t = i / bodySegments;
    bodyPoints.push(new THREE.Vector3((t - 0.5) * 4, 0, 0));
  }
  const bodyCurve = new THREE.CatmullRomCurve3(bodyPoints);
  const bodyGeo = new THREE.TubeGeometry(bodyCurve, bodySegments, 0.25, 8, false);
  const body = new THREE.Mesh(bodyGeo, dragonMat);
  group.add(body);

  // ── Head: icosahedron with horns ──
  const headGeo = new THREE.IcosahedronGeometry(0.3, 1);
  const head = new THREE.Mesh(headGeo, dragonMat);
  head.position.set(2.2, 0, 0);
  group.add(head);

  // Horns
  const hornGeo = new THREE.ConeGeometry(0.05, 0.3, 5);
  const hornL = new THREE.Mesh(hornGeo, dragonMat);
  hornL.position.set(2.15, 0.35, 0.1);
  hornL.rotation.z = -0.3;
  group.add(hornL);
  const hornR = new THREE.Mesh(hornGeo, dragonMat);
  hornR.position.set(2.15, 0.35, -0.1);
  hornR.rotation.z = -0.3;
  group.add(hornR);

  // ── Wings: two large planes with noise ──
  const wingGeo = new THREE.PlaneGeometry(1.5, 0.8, 8, 4);
  const wingMat = new THREE.MeshStandardNodeMaterial({
    metalness: 0.2, roughness: 0.5, color: 0x2a0a2a,
    side: THREE.DoubleSide,
    transparent: true, opacity: 0.85,
  });
  wingMat.emissiveNode = Fn(() => {
    const glow = add(float(0.1), mul(u.uBass, float(0.3)));
    return vec4(mul(float(0.3), glow), mul(float(0.5), glow), mul(float(0.8), glow), float(1.0));
  })();
  const wingL = new THREE.Mesh(wingGeo, wingMat);
  wingL.position.set(0.8, 0.3, 0.7);
  group.add(wingL);
  const wingR = new THREE.Mesh(wingGeo, wingMat);
  wingR.position.set(0.8, 0.3, -0.7);
  group.add(wingR);

  // ── Scales: instanced small icos ──
  const scaleCount = 40;
  const scaleGeo = new THREE.IcosahedronGeometry(0.04, 0);
  const scales = new THREE.InstancedMesh(scaleGeo, dragonMat, scaleCount);
  scales.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(scales);

  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const audio: AudioRef = {
      bass: u.uBass.value ?? 0, beat: u.uBeat.value ?? 0, mid: u.uMid.value ?? 0,
      level: ((u.uBass.value ?? 0) + (u.uMid.value ?? 0)) * 0.5,
    };
    lighting.update(audio);

    // Body undulation: rebuild tube path with sine wave
    const waveAmp = 0.3 + (u.uBass.value ?? 0) * 0.4;
    const waveSpeed = 1.5 + (u.uMid.value ?? 0) * 1.5;
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= bodySegments; i++) {
      const segT = i / bodySegments;
      const x = (segT - 0.5) * 4;
      const y = Math.sin(segT * 6 + t * waveSpeed) * waveAmp * segT;
      const z = Math.cos(segT * 4 + t * waveSpeed * 0.7) * waveAmp * 0.5 * segT;
      points.push(new THREE.Vector3(x, y, z));
    }
    bodyGeo.dispose();
    const newCurve = new THREE.CatmullRomCurve3(points);
    const newGeo = new THREE.TubeGeometry(newCurve, bodySegments, 0.25, 8, false);
    body.geometry = newGeo;
    // Update head position to follow body end
    head.position.copy(points[bodySegments]).add(new THREE.Vector3(0.3, 0.1, 0));
    head.rotation.y = Math.sin(t * 2) * 0.3 + (u.uBeat.value ?? 0) * 0.3;
    hornL.position.copy(head.position).add(new THREE.Vector3(-0.05, 0.35, 0.1));
    hornR.position.copy(head.position).add(new THREE.Vector3(-0.05, 0.35, -0.1));

    // Wing flap
    const flap = Math.sin(t * 3) * 0.5 + (u.uBass.value ?? 0) * 0.4;
    wingL.rotation.x = -flap;
    wingR.rotation.x = flap;
    wingL.position.x = points[Math.floor(bodySegments * 0.6)].x;
    wingR.position.x = wingL.position.x;

    // Scales along body
    for (let i = 0; i < scaleCount; i++) {
      const segT = (i / scaleCount) * 0.9;
      const idx = Math.floor(segT * bodySegments);
      const p = points[idx] || points[0];
      const wave = Math.sin(t * 5 + i) * 0.02 * (u.uTreble.value ?? 0);
      pivot.position.set(p.x, p.y + 0.2 + wave, p.z);
      pivot.rotation.set(t * 0.5, i, 0);
      pivot.scale.setScalar(0.5 + Math.sin(t * 3 + i) * 0.2);
      pivot.updateMatrix();
      scales.setMatrixAt(i, pivot.matrix);
    }
    scales.instanceMatrix.needsUpdate = true;

    group.rotation.y = Math.sin(t * 0.1) * 0.2;
  };

  const dispose = (): void => {
    lighting.dispose();
    body.geometry.dispose(); dragonMat.dispose();
    headGeo.dispose(); hornGeo.dispose();
    wingGeo.dispose(); wingMat.dispose();
    scaleGeo.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Fractal Dragon' };
}